import React from 'react';
import {
  PolicyImpactResults,
  gridCarbonIntensityKgPerKwhForPolicy,
} from '../../services/policyImpactService';
import './VehicleElectrificationPolicy.css';

interface VehicleElectrificationPolicyProps {
  currentEVPercent: number; // 2045 BAU EV percentage
  currentEVPercent2023?: number; // 2023 EV percentage (for display)
  /** Beyond-BAU slider position 0–100 (0=BAU, 100=100% ZEV). */
  beyondBauPercent?: number;
  targetEVPercent: number; // Absolute fleet EV% by 2045 after mapping from beyond-BAU slider
  location: string; // Location name
  results: PolicyImpactResults | null; // Policy impact results for calculations
  emfacVersion?: '2021' | '2025'; // EMFAC version used for calculations
  /** Align modeled grid intensity with main policy calc (baseline kW × multiplier). */
  solarCapacityMultiplier?: number;
}

const VehicleElectrificationPolicy: React.FC<VehicleElectrificationPolicyProps> = ({
  currentEVPercent, // 2045 BAU EV%
  currentEVPercent2023, // 2023 EV%
  beyondBauPercent,
  targetEVPercent, // Absolute 2045 fleet EV%
  location,
  results,
  emfacVersion = '2025', // Default to EMFAC2025
  solarCapacityMultiplier = 1,
}) => {
  // Constants used in calculations (EV kWh/mile must match policyImpactService CONSTANTS.EV_KWH_PER_MILE)
  const EV_KWH_PER_MILE = 0.3; // Average EV efficiency
  const ICE_IMPROVEMENT_FACTOR = 0.95; // Future ICE efficiency improvement
  const FALLBACK_GRID_INTENSITY_KG_PER_KWH = 0.2; // when baseline CI unavailable

  const gPerKwh = results?.baseline.electricityCarbonIntensityGPerKwh;
  const baselineYear = results?.baseline.baselineInventoryYear ?? 2023;
  const inventoryGridIntensityKgPerKwh =
    gPerKwh != null && gPerKwh > 0 ? gPerKwh / 1000 : FALLBACK_GRID_INTENSITY_KG_PER_KWH;

  // Calculate EV contribution from results
  const evContribution = results?.contributionsByPolicy.evAdoption || 0;
  
  // Get baseline data from results
  const baselineVMT = results?.baseline.vmt || 0;
  const baselineLightDutyGHG = results?.baseline.lightDutyGHG || 0;
  const baselineGhgPerMile = baselineVMT > 0 ? baselineLightDutyGHG / baselineVMT : 0;
  const solarKw =
    (results?.baseline.solarCapacityKw ?? 0) * (solarCapacityMultiplier || 1);
  const pop = results?.baseline.population ?? 0;

  const modeledGridIntensityKgPerKwh = results
    ? gridCarbonIntensityKgPerKwhForPolicy(solarKw, pop, gPerKwh ?? 0)
    : inventoryGridIntensityKgPerKwh;
  
  // Calculate emission factors
  const iceGhgPerMile = baselineGhgPerMile * ICE_IMPROVEMENT_FACTOR; // tons CO2/mile
  const evGhgPerMileKg = modeledGridIntensityKgPerKwh * EV_KWH_PER_MILE; // kg CO2/mile
  const evGhgPerMile = evGhgPerMileKg / 1000; // tons CO2/mile
  
  // Calculate fleet-weighted emission factor at target
  const icePercent = 100 - targetEVPercent;
  const fleetWeightedGhgPerMile = (icePercent / 100 * iceGhgPerMile) + (targetEVPercent / 100 * evGhgPerMile);

  return (
    <div className="vehicle-electrification-policy">
      <div className="policy-details">
        <div className="details-section">
          <h4>How the slider works</h4>
          <p>
            The Vehicle Electrification slider runs from <strong>0%</strong> (left) to <strong>100%</strong> (right).
            It is <em>not</em> “percent of cars that are EVs today.” It measures how much of the remaining path
            from EMFAC business-as-usual (BAU) to full ZEV you choose for {location} by 2045.
          </p>
          <div className="calculation-results">
            <div className="result-item">
              <span className="result-label">Slider at 0% (left)</span>
              <span className="result-value">{currentEVPercent.toFixed(1)}% fleet EV in 2045</span>
              <div className="assumption-description">
                Matches EMFAC{emfacVersion || '2025'} BAU for {location}: no extra local ZEV ambition beyond that pathway.
                Chart gray (&quot;Incremental vs BAU&quot;) from this lever is ~0.
              </div>
            </div>
            <div className="result-item">
              <span className="result-label">Slider at 100% (right)</span>
              <span className="result-value">100% fleet EV in 2045</span>
              <div className="assumption-description">
                Full light-duty ZEV by 2045—the maximum for this lever.
              </div>
            </div>
            <div className="result-item">
              <span className="result-label">Your slider setting</span>
              <span className="result-value">
                {beyondBauPercent != null ? `${Number(beyondBauPercent).toFixed(1)}%` : '—'}
              </span>
              <div className="assumption-description">
                Share of the gap from BAU ({currentEVPercent.toFixed(1)}%) to 100% ZEV that you close.
                Formula: fleet EV% = {currentEVPercent.toFixed(1)}% + (100% − {currentEVPercent.toFixed(1)}%) × (slider ÷ 100).
              </div>
            </div>
            <div className="result-item">
              <span className="result-label">Implied fleet EV% in 2045 (modeled)</span>
              <span className="result-value">{Number(targetEVPercent).toFixed(1)}%</span>
              <div className="assumption-description">
                Absolute light-duty EV share used in the GHG forecast for this scenario.
              </div>
            </div>
            <div className="result-item">
              <span className="result-label">Observed EV share ({location}, 2023)</span>
              <span className="result-value">{(currentEVPercent2023 || 0).toFixed(2)}%</span>
              <div className="assumption-description">
                Present-day share for context only—the slider endpoints are BAU 2045 and 100% ZEV, not 2023.
              </div>
            </div>
          </div>
        </div>

        <div className="details-section">
          <h4>Policy Description</h4>
          <p>
            Electric vehicles produce zero direct tailpipe emissions and, when powered by the electricity grid,
            typically have lower lifecycle emissions than ICE vehicles. As the grid decarbonizes, EV emissions
            decrease further. This policy estimates GHG reductions from higher ZEV adoption than EMFAC BAU,
            using the fleet mix and modeled grid carbon intensity.
          </p>
        </div>

        <div className="details-section">
          <h4>Critical Assumptions</h4>
          <div className="calculation-results">
            <div className="result-item">
              <span className="result-label">EV Efficiency:</span>
              <span className="result-value">{EV_KWH_PER_MILE} kWh/mile</span>
            </div>
            <div className="result-item">
              <span className="result-label">ICE Improvement Factor:</span>
              <span className="result-value">{ICE_IMPROVEMENT_FACTOR}</span>
              <div className="assumption-description">
                Assumes future ICE vehicles will be 5% more efficient than current baseline
              </div>
            </div>
            <div className="result-item">
              <span className="result-label">Inventory grid carbon intensity ({location}, {baselineYear}):</span>
              <span className="result-value">
                {inventoryGridIntensityKgPerKwh.toFixed(3)} kg CO₂e/kWh
              </span>
              <div className="assumption-description">
                Location-specific electricity emissions factor, consistent with Buildings electricity in this tool
                (same source as baseline inventory). If data are missing, a 0.2 kg/kWh placeholder is used.
              </div>
            </div>
            <div className="result-item">
              <span className="result-label">Modeled grid intensity for EV charging:</span>
              <span className="result-value">
                {modeledGridIntensityKgPerKwh.toFixed(3)} kg CO₂e/kWh
              </span>
              <div className="assumption-description">
                Inventory intensity adjusted for estimated local solar penetration and your solar capacity setting,
                matching the vehicle electrification calculation. Grid Decarbonization further reduces grid emissions by 2045
                in the scenario summary.
              </div>
            </div>
          </div>
        </div>

        <div className="details-section">
          <h4>Calculation Results</h4>
          <div className="calculation-results">
            <div className="result-item">
              <span className="result-label">Baseline Light-Duty VMT:</span>
              <span className="result-value">{baselineVMT.toLocaleString(undefined, {maximumFractionDigits: 0})} miles/year</span>
            </div>
            <div className="result-item">
              <span className="result-label">Baseline GHG per Mile (ICE):</span>
              <span className="result-value">{(baselineGhgPerMile * 1000).toFixed(4)} kg CO₂e/mile</span>
            </div>
            <div className="result-item">
              <span className="result-label">ICE GHG per Mile (2023 baseline):</span>
              <span className="result-value">{(iceGhgPerMile * 1000).toFixed(4)} kg CO₂e/mile</span>
            </div>
            <div className="result-item">
              <span className="result-label">EV GHG per Mile (grid-powered):</span>
              <span className="result-value">{(evGhgPerMile * 1000).toFixed(4)} kg CO₂e/mile</span>
            </div>
            <div className="result-item">
              <span className="result-label">Fleet-Weighted GHG per Mile (at {targetEVPercent}% EVs):</span>
              <span className="result-value">{(fleetWeightedGhgPerMile * 1000).toFixed(4)} kg CO₂e/mile</span>
            </div>
            <div className="result-item highlight">
              <span className="result-label">Net GHG Reduction:</span>
              <span className="result-value">{evContribution.toLocaleString(undefined, {maximumFractionDigits: 0})} MT CO₂e</span>
            </div>
          </div>
        </div>

        <div className="details-section">
          <h4>Methodology</h4>
          <p>
            The GHG reduction from vehicle electrification is calculated by comparing fleet-weighted emissions
            per mile at the target EV adoption rate versus the baseline fleet composition. The calculation accounts for:
          </p>
          <ul>
            <li>Current EV adoption rate in the selected location</li>
            <li>Target EV adoption rate by 2045 (user-selected)</li>
            <li>ICE vehicle emission factors from EMFAC data (adjusted for future efficiency improvements)</li>
            <li>EV emission factors based on grid carbon intensity and EV energy efficiency</li>
            <li>Fleet-weighted average emissions per mile</li>
            <li>Projected vehicle miles traveled (VMT)</li>
          </ul>
          <p>
            As the electricity grid decarbonizes (through the Grid Decarbonization policy), EV emissions per mile decrease,
            further reducing total GHG emissions from the transportation sector.
          </p>
        </div>
      </div>
    </div>
  );
};

export default VehicleElectrificationPolicy;

